import { memo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { AuraLayout } from "../../../components/AuraLayout";
import { useStore } from "../../../store/globalState";
import { errorInitialState } from "../../../constants";
import type { IErrorState } from "../interfaces/error";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import {
  appendAnalyticsHandoff,
  getAnalyticsSession,
  getEvents,
  safeCapture,
} from "../../../analytics";
import { useLiveExitUrl } from "../../../hooks/useLiveExitUrl";
import { isHttpUrl } from "../../../utils/live-exit-url";
import { LINK_ERROR_CODE } from "../../../utils/commons";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";

export const ErrorPage = memo(() => {
  const { t } = useTranslation();
  const { theme, errorState } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  const error =
    (location.state as IErrorState) || errorState || errorInitialState;
  const {
    title: errorTitle,
    subTitle = "",
    message,
    btnText,
    isOut,
    pageUrl,
    errorCode,
  } = error;
  const liveCallbackUrl = useLiveExitUrl();
  const isDeadOrMissing =
    errorCode === LINK_ERROR_CODE.LINK_NOT_FOUND ||
    errorCode === LINK_ERROR_CODE.LINK_EXPIRED ||
    errorCode === LINK_ERROR_CODE.LINK_REVOKED;
  const explicitHttpUrl =
    typeof pageUrl === "string" && isHttpUrl(pageUrl) ? pageUrl : undefined;
  const liveReturnUrl =
    explicitHttpUrl ??
    (!pageUrl && !isDeadOrMissing ? liveCallbackUrl : undefined);

  useEffect(() => {
    safeCapture(getEvents().ERROR_SCREEN, {
      error_code: errorCode,
    });
  }, [errorCode]);

  const leaveToExternalUrl = (url: string) => {
    const { sessionId, distinctId } = getAnalyticsSession();
    const handoffUrl =
      sessionId && distinctId
        ? appendAnalyticsHandoff(url, { sessionId, distinctId })
        : url;
    window.location.href = handoffUrl;
  };

  const handleRetry = () => {
    safeCapture(getEvents().EXIT_CLICK, { screen: "error" });
    if (liveReturnUrl) {
      leaveToExternalUrl(liveReturnUrl);
      return;
    }
    const isExternalUrl =
      typeof pageUrl === "string" && isHttpUrl(pageUrl);

    if ((isOut || isExternalUrl) && pageUrl) {
      leaveToExternalUrl(`${pageUrl}`);
      return;
    }

    const newPageUrl = pageUrl || -1;
    navigate(newPageUrl as unknown as string);
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <ErrorIcon size={60} colors={iconColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={errorTitle || t("error.title")}
              highlightColor={title}
              secudnary={subTitle || undefined}
              secudnaryColor={title}
            />
            <BodyCopy textColor={bodyCopy}>
              {message || t("error.tryAgainLater")}
            </BodyCopy>
          </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          {(btnText || pageUrl || liveReturnUrl) && (
            <Button
              size="small"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleRetry}
            >
              {liveReturnUrl
                ? btnText || t("error.returnToApp")
                : btnText || t("common.retry")}
            </Button>
          )}
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
